'use client';

import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { FaCrosshairs, FaEarListen, FaFireFlameCurved, FaGem, FaHand, FaHandHoldingHeart, FaPrescriptionBottleMedical, FaSpa } from "react-icons/fa6";
import { Service } from "@/types";
import { Button } from "../ui/button";
import { DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from "../ui/drawer";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { ServiceKey } from "@/data/services";

export type { ServiceKey };

export const servicesData: Service[] = [
  {
    id: 1,
    slug: "doctors-consultation",
    icon: <FaPrescriptionBottleMedical className="h-8 w-8" />
  },
  {
    id: 2,
    slug: "acupuncture",
    icon: <FaCrosshairs className="h-8 w-8" />
  },
  {
    id: 3,
    slug: "acupressure",
    icon: <FaHand className="h-8 w-8" />
  },
  {
    id: 8,
    slug: "massage",
    icon: <FaHandHoldingHeart className="h-8 w-8" />
  },
  {
    id: 4,
    slug: "auriculotherapy",
    icon: <FaEarListen className="h-8 w-8" />
  },
  {
    id: 5,
    slug: "moxibustion",
    icon: <FaFireFlameCurved className="h-8 w-8" />
  },
  {
    id: 6,
    slug: "vacuum-therapy",
    icon: <FaPrescriptionBottleMedical className="h-8 w-8" />
  },
  {
    id: 7,
    slug: "stone-therapy",
    icon: <FaGem className="h-8 w-8" />
  },
  {
    id: 9,
    slug: "cosmetology",
    icon: <FaSpa className="h-8 w-8" />
  }
];

export default function ServicesSection() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const t = useTranslations('services_section');
  const serviceLink = useCallback((s: Service) => `/services/${s.slug}`, []);
  const serviceTitle = useCallback((slug: ServiceKey) => t(`service_items.${slug}.title`), [t]);
  const serviceDescription = useCallback((slug: ServiceKey) => t(`service_items.${slug}.description`), [t]);

  const handleClick = useCallback((service: Service) => {
    if (isMobile) {
      <DrawerContent>
        {service.icon}
        <DrawerHeader>
          <DrawerTitle>{serviceTitle(service.slug)}</DrawerTitle>
        </DrawerHeader>
        <p>{serviceDescription(service.slug)}</p>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="outline">{t('close')}</Button>
          </DrawerClose>
          <Button variant="default">{t('book_now')}</Button>
        </DrawerFooter>
      </DrawerContent>
    } else {
      router.push(serviceLink(service));
    }
  }, [isMobile, serviceTitle, serviceDescription, t, router, serviceLink]);

  return (
    servicesData.map((s) => (
      <div key={s.id} className="group block" onClick={() => handleClick(s)}>
        <article className="h-full rounded-xl xs:max-w-[220px] md:max-w-full bg-background p-6 shadow-md ring-1 ring-border transition hover:shadow-xl text-center">
          <div
            className="hover:shadow-md
              w-16 h-16 flex items-center justify-center
              transform-3d relative z-2
              mx-auto mb-5 bg-[#624d65] rounded-md
              transition duration-300 ease-out
              text-white shadow-sm
              group-hover:-translate-y-0.5 group-hover:shadow-md"
          >
            {s.icon}
          </div>
          <h4 className="text-lg font-semibold leading-tight">{serviceTitle(s.slug)}</h4>
          <p className="mt-2 text-sm text-muted-foreground line-clamp-4">{serviceDescription(s.slug)}</p>
        </article>
      </div>
    ))
  );
}
